import type { UserSummary } from '@school-erp/types';
import { createUserSchema } from '@school-erp/validation';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

const PAGE_SIZE = 10;

export function UsersPage() {
  const { can, user: me } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['users', { page, search }],
    queryFn: ({ signal }) =>
      apiList<UserSummary>('/users', {
        query: { page, pageSize: PAGE_SIZE, q: search.trim() },
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
      api(`/users/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const totalPages = users.data?.meta.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('users.title')}</h1>
        {can('users.create') && (
          <button
            type="button"
            onClick={() => setAdding((open) => !open)}
            aria-expanded={adding}
            className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900"
          >
            {adding ? t('users.cancel') : t('users.add')}
          </button>
        )}
      </div>

      {notice && (
        <p role="status" className="rounded bg-green-50 p-3 text-sm text-green-800">
          {notice}
        </p>
      )}

      {adding && (
        <CreateUserForm
          onCreated={() => {
            setAdding(false);
            setNotice(t('users.created'));
            void queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      <div>
        <label htmlFor="user-search" className="block text-sm font-medium">
          {t('users.search')}
        </label>
        <input
          id="user-search"
          type="search"
          value={search}
          placeholder={t('users.searchPlaceholder')}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="mt-1 w-full max-w-sm rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        />
      </div>

      {users.isPending && <p role="status">{t('users.loading')}</p>}
      {users.isError && (
        <p role="alert" className="text-red-700">
          {t('users.error')}
        </p>
      )}

      {users.data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('users.name')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.email')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.roles')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.status')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.data.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-600">
                      {t('users.empty')}
                    </td>
                  </tr>
                )}
                {users.data.data.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 font-medium">{row.fullName}</td>
                    <td className="px-4 py-3">{row.email}</td>
                    <td className="px-4 py-3">
                      {row.roles.length
                        ? row.roles.map((r) => r.name).join(', ')
                        : t('users.noRoles')}
                    </td>
                    <td className="px-4 py-3">
                      {row.status === 'active' ? t('users.active') : t('users.disabled')}
                    </td>
                    <td className="px-4 py-3">
                      {can('users.update') && row.id !== me?.id && (
                        <button
                          type="button"
                          disabled={setStatus.isPending}
                          onClick={() => {
                            const next = row.status === 'active' ? 'disabled' : 'active';
                            if (
                              next === 'active' ||
                              window.confirm(t('users.confirmDisable', { name: row.fullName }))
                            ) {
                              setStatus.mutate({ id: row.id, status: next });
                            }
                          }}
                          className="rounded border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50"
                        >
                          {row.status === 'active' ? t('users.disable') : t('users.enable')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav aria-label="Pagination" className="flex items-center justify-between text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
            >
              {t('users.prev')}
            </button>
            <span>{t('users.page', { page, total: totalPages })}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
            >
              {t('users.next')}
            </button>
          </nav>
        </>
      )}
    </div>
  );
}

function CreateUserForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ fullName: '', email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: object) => api('/users', { method: 'POST', body }),
    onSuccess: onCreated,
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'EMAIL_TAKEN') {
        setErrors({ email: t('users.emailTaken') });
      } else {
        setFormError(t('auth.genericError'));
      }
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const parsed = createUserSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message]),
        ),
      );
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  const bind = (name: keyof typeof form) => ({
    name,
    value: form[name],
    error: errors[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [name]: e.target.value })),
  });

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
    >
      <TextField label={t('users.fullName')} autoComplete="off" required {...bind('fullName')} />
      <TextField
        label={t('users.email')}
        type="email"
        autoComplete="off"
        required
        {...bind('email')}
      />
      <TextField
        label={t('users.initialPassword')}
        type="password"
        autoComplete="new-password"
        hint={t('auth.passwordHint')}
        required
        {...bind('password')}
      />
      <div className="sm:col-span-3">
        {formError && (
          <p role="alert" className="mb-2 text-sm text-red-700">
            {formError}
          </p>
        )}
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
        >
          {create.isPending ? t('users.creating') : t('users.create')}
        </button>
      </div>
    </form>
  );
}
